import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Footer from '../components/layout/footer';
import { Gallery } from '../components/product/gallery';
import { ProductProvider } from '../components/product/product-context';
import { useLanguage } from '../context/LanguageContext';
import {
  getProductBySlug,
  getProductImages,
  getProductVariants,
  getProductsByCategoryId,
  getProductShotsByProductIds,
} from '../lib/maison-adji-api';
import { useMaisonAdjiCart } from '../context/MaisonAdjiCartContext';
import { usePreferredCurrency } from '../context/CurrencySettingsContext';

/** Fiche produit pour les produits Maison Adji (Supabase) */
function MaisonAdjiProductView({
  slug,
  onNotFound,
}: {
  slug: string;
  onNotFound: () => void;
}) {
  const { t, localize } = useLanguage();
  const { addItem: addToMaisonAdjiCart } = useMaisonAdjiCart();
  const { preferredCurrency } = usePreferredCurrency();
  const [product, setProduct] = useState<any>(null);
  const [images, setImages] = useState<{ url: string; alt_text: string | null }[]>([]);
  const [_variants, setVariants] = useState<any[]>([]);
  const [related, setRelated] = useState<any[]>([]);
  const [relatedShots, setRelatedShots] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const p = await getProductBySlug(slug);
      if (!p) {
        setLoading(false);
        onNotFound();
        return;
      }
      setProduct(p);
      const [imgs, vars] = await Promise.all([
        getProductImages(p.id),
        getProductVariants(p.id),
      ]);
      setImages(imgs.map((i) => ({ url: i.url, alt_text: i.alt_text })));
      setVariants(vars);

      if (p.category_id) {
        const sameCategory = await getProductsByCategoryId(p.category_id);
        const list = sameCategory.filter((r) => r.id !== p.id).slice(0, 5);
        setRelated(list);
        if (list.length > 0) {
          const shots = await getProductShotsByProductIds(list.map((r) => r.id));
          const firstUrl: Record<string, string> = {};
          for (const id of list.map((r) => r.id)) {
            const arr = shots[id];
            if (arr?.length) firstUrl[id] = arr[0].url;
          }
          setRelatedShots(firstUrl);
        }
      }
      setLoading(false);
    }
    load();
  }, [slug, onNotFound]);

  if (loading || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  const name = localize(product, 'name') || product.name;
  const description = localize(product, 'description') || product.description || '';
  const priceXof = product.price_xof != null ? Number(product.price_xof) : null;
  const priceEur = product.price_eur != null ? Number(product.price_eur) : null;
  const price =
    preferredCurrency === 'XOF'
      ? (priceXof ?? priceEur ?? 0)
      : (priceEur ?? priceXof ?? 0);
  const currency =
    preferredCurrency === 'XOF'
      ? (priceXof != null ? 'XOF' : 'EUR')
      : (priceEur != null ? 'EUR' : 'XOF');

  const galleryImages =
    images.length > 0
      ? images.map((i) => ({ src: i.url, altText: i.alt_text || name }))
      : [{ src: '/assets/images/maisonadjiwhite.png', altText: name }];

  return (
    <ProductProvider>
    <div className="bg-neutral-950 min-h-screen pt-24 md:pt-28 pb-20">
      <div className="mx-auto max-w-6xl px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          <div className="flex-1">
            <Gallery images={galleryImages} />
          </div>
          <div className="lg:w-[380px] flex-shrink-0">
            <h1 className="font-['Sora'] text-2xl md:text-3xl text-white font-light tracking-tight mb-4">
              {name}
            </h1>
            {description && (
              <p className="font-['Rubik'] text-white/60 text-sm leading-relaxed mb-6">
                {description}
              </p>
            )}
            <div className="flex items-baseline gap-3 mb-8">
              <span className="font-['Sora'] text-xl text-white">
                {currency === 'EUR'
                  ? `${price.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                  : `${price.toLocaleString('fr-FR')} XOF`}
              </span>
            </div>
            <button
              type="button"
              className="w-full py-4 font-['Rubik'] text-xs tracking-[0.2em] uppercase border border-white/30 text-white hover:bg-white hover:text-black transition-colors"
              onClick={() =>
                addToMaisonAdjiCart({
                  productId: product.id,
                  slug: product.slug,
                  name,
                  nameEn: product.name_en ?? null,
                  priceXof: priceXof ?? null,
                  priceEur: priceEur ?? null,
                  imageUrl: galleryImages[0]?.src ?? '',
                })
              }
            >
              {t('product.add_to_cart')}
            </button>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16 pt-16 mb-20 border-t border-white/10">
            <h2 className="font-['Sora'] text-lg text-white/80 mb-6">
              {t('product.similar')}
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/product/${r.slug}`}
                    className="block aspect-[3/4] overflow-hidden bg-neutral-900 rounded-sm"
                  >
                    {relatedShots[r.id] ? (
                      <img
                        src={relatedShots[r.id]}
                        alt={localize(r, 'name') || r.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20 font-['Sora'] text-sm">
                        {localize(r, 'name') || r.name}
                      </div>
                    )}
                  </Link>
                  <p className="mt-2 font-['Rubik'] text-sm text-white/60 truncate">
                    {localize(r, 'name') || r.name}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <Footer />
    </div>
    </ProductProvider>
  );
}

/** Fiche produit legacy (burokshop) */
export default function ProductPage() {
  const { handle } = useParams<{ handle: string }>();
  const [notFound, setNotFound] = useState(false);

  if (!handle) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-950">
        <div className="text-white/60">Produit non trouvé</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-neutral-950 gap-4">
        <p className="text-white/60">Produit non trouvé</p>
        <Link to="/collections" className="font-['Rubik'] text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white">
          Retour aux produits
        </Link>
      </div>
    );
  }

  return (
    <MaisonAdjiProductView
      slug={handle}
      onNotFound={() => setNotFound(true)}
    />
  );
}
